import { useEffect, useRef } from 'react';
import { hasMode } from '../../utils/check-mode';
import { isCookiesDisabled } from '../../utils/helpers';
import { t } from '../../utils/i18n';

export const InputFieldStyles = `
    .gh-portal-input-section.hidden {
        display: none;
    }
    .gh-portal-input {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;

        display: block;
        box-sizing: border-box;
        font-size: 1.5rem;
        color: inherit;
        background: transparent;
        outline: none;
        border: 1px solid var(--grey11);
        border-radius: 6px;
        width: 100%;
        height: 44px;
        padding: 0 12px;
        margin-bottom: 16px;
        letter-spacing: 0.2px;
        transition: border-color 0.25s ease-in-out;
    }

    .gh-portal-input-labelcontainer {
        display: flex;
        justify-content: space-between;
        width: 100%;
    }

    .gh-portal-input-labelcontainer p {
        color: var(--red);
        font-size: 1.3rem;
        letter-spacing: 0.35px;
        line-height: 1.6em;
        margin-bottom: 0;
    }

    .gh-portal-input-label.hidden {
        display: none;
    }

    .gh-portal-input:focus {
        border-color: var(--grey8);
    }

    .gh-portal-input.error {
        border-color: var(--red);
    }

    /* Keyed on its own class rather than the element: the gift message and the
       cancellation reason are textareas with the base class too, and keep their look. */
    .gh-portal-input.gh-portal-input-textarea {
        height: auto;
        min-height: 88px;
        padding: 10px 12px;
        line-height: 1.4em;
        resize: vertical;
    }

    /* The chevron is drawn here because the appearance reset above takes the native one
       with it, and the padding keeps the value clear of it. */
    select.gh-portal-input {
        padding-inline-end: 36px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23aeaeae' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 12px center;
        cursor: pointer;
    }

    html[dir="rtl"] select.gh-portal-input {
        background-position: left 12px center;
    }

    /* With nothing chosen the empty option shows, and reads as placeholder text. The
       options do not inherit that grey: the browsers that paint the list from computed
       styles would otherwise show every country as placeholder text. CanvasText follows
       the list's own light or dark scheme. */
    select.gh-portal-input.placeholder {
        color: var(--grey8);
    }

    select.gh-portal-input option {
        color: CanvasText;
    }

    /* Several inputs presented as one field, the way an address is filled in at checkout.
       Neighbours overlap by a pixel so their borders read as one divider, and the focused
       or invalid input is lifted so its own border shows whole. */
    .gh-portal-input-group {
        margin-bottom: 16px;
    }

    .gh-portal-input-group .gh-portal-input {
        position: relative;
        border-radius: 0;
        margin-bottom: 0;
    }

    .gh-portal-input-group .gh-portal-input:focus,
    .gh-portal-input-group .gh-portal-input.error {
        z-index: 1;
    }

    .gh-portal-input-group-row {
        display: flex;
    }

    .gh-portal-input-group-row + .gh-portal-input-group-row {
        margin-top: -1px;
    }

    .gh-portal-input-group-row .gh-portal-input-section {
        flex: 1;
        min-width: 0;
    }

    .gh-portal-input-group-row .gh-portal-input-section + .gh-portal-input-section {
        margin-inline-start: -1px;
    }

    .gh-portal-input-group-row:first-child .gh-portal-input-section:first-child .gh-portal-input {
        border-start-start-radius: 6px;
    }

    .gh-portal-input-group-row:first-child .gh-portal-input-section:last-child .gh-portal-input {
        border-start-end-radius: 6px;
    }

    .gh-portal-input-group-row:last-child .gh-portal-input-section:first-child .gh-portal-input {
        border-end-start-radius: 6px;
    }

    .gh-portal-input-group-row:last-child .gh-portal-input-section:last-child .gh-portal-input {
        border-end-end-radius: 6px;
    }

    .gh-portal-input::placeholder {
        color: var(--grey8);
    }

    /* The attribute, not :read-only: a select counts as read-only to that pseudo-class
       and would take the disabled look while being perfectly usable. */
    .gh-portal-popup-container:not(.preview) .gh-portal-input:disabled,
    .gh-portal-popup-container:not(.preview) .gh-portal-input[readonly] {
        /* The colour alone: the shorthand would take a disabled select's chevron with it. */
        background-color: var(--grey13);
        color: var(--grey9);
        box-shadow: none;
    }

    .gh-portal-popup-container:not(.preview) .gh-portal-input:disabled::placeholder,
    .gh-portal-popup-container:not(.preview) .gh-portal-input[readonly]::placeholder {
        color: var(--grey9);
    }
`;

function InputError({ message, style }) {
  if (!message) {
    return null;
  }
  return (
    <p
      style={{
        ...(style || {}),
      }}
    >
      {message}
    </p>
  );
}

function InputField({
  name,
  id,
  hidden,
  label,
  hideLabel,
  type,
  value,
  placeholder,
  disabled = false,
  readOnly = false,
  options,
  onChange = () => {},
  onBlur = () => {},
  onKeyDown = () => {},
  tabIndex,
  maxLength,
  autoFocus,
  errorMessage,
  // Marked wrong without saying why here. A composite's inputs share one field, and its
  // reasons are listed under the group rather than wedged between its rows.
  invalid = false,
  // What says why, when it is not printed beside the input: a composite's reasons are
  // listed under the group, and an input has to point at them to be read out with it.
  describedBy,
}) {
  const fieldNode = useRef(null);
  id = id || `input-${name}`;
  const sectionClasses = hidden ? 'gh-portal-input-section hidden' : 'gh-portal-input-section';
  const labelClasses = hideLabel ? 'gh-portal-input-label hidden' : 'gh-portal-input-label';
  const inputClasses = errorMessage || invalid ? 'gh-portal-input error' : 'gh-portal-input';
  if (isCookiesDisabled()) {
    disabled = true;
  }

  // Disable all input fields in preview mode
  if (hasMode(['preview'])) {
    disabled = true;
  }

  let autoComplete = '';
  let autoCorrect = '';
  let autoCapitalize = '';
  let inputMode;
  let pattern;
  switch (id) {
    case 'input-email':
      autoComplete = 'off';
      autoCorrect = 'off';
      autoCapitalize = 'off';
      break;
    case 'input-name':
      autoComplete = 'off';
      autoCorrect = 'off';
      break;
    case 'input-otc':
      autoComplete = 'one-time-code';
      autoCorrect = 'off';
      autoCapitalize = 'off';
      inputMode = 'numeric';
      pattern = '[0-9]*';
      placeholder ??= '• • • • • •';

      break;
    default:
      break;
  }
  useEffect(() => {
    if (autoFocus) {
      fieldNode.current.focus();
    }
  }, [autoFocus]);
  const fieldProps = {
    'data-test-input': id,
    ref: fieldNode,
    id,
    className: inputClasses,
    name,
    value,
    placeholder,
    onChange: (e) => onChange(e, name),
    onBlur: (e) => onBlur(e, name),
    disabled,
    readOnly,
    tabIndex,
    maxLength,
    'aria-label': label,
    'aria-invalid': errorMessage || invalid ? true : undefined,
    'aria-describedby': describedBy,
  };
  return (
    <section className={sectionClasses}>
      <div className="gh-portal-input-labelcontainer">
        <label htmlFor={id} className={labelClasses}>
          {' '}
          {label}{' '}
        </label>
        <InputError message={errorMessage} name={name} />
      </div>
      {type === 'textarea' ? (
        // No onKeyDown: Enter adds a line here, where in an input it submits the form.
        <textarea {...fieldProps} className={`${inputClasses} gh-portal-input-textarea`} />
      ) : type === 'select' ? (
        // A select cannot be read-only, so a value the member may not change is disabled
        // instead. The empty option stands for no value: with nothing chosen it carries
        // the placeholder, since the closed control can only show an option's text, and
        // is not a choice, so it is hidden from the list where the browser allows
        // (Safari ignores `hidden` on an option and shows it greyed and unselectable
        // instead). Once something is chosen it is offered as the way to clear it,
        // parenthesised the way lists mark the row that is not one of the values, since
        // an option's text is all a native list can style across browsers.
        <select
          {...fieldProps}
          className={value ? inputClasses : `${inputClasses} placeholder`}
          disabled={disabled || readOnly}
        >
          {value ? (
            <option value="">{t('(None)')}</option>
          ) : (
            <option value="" disabled hidden>
              {placeholder}
            </option>
          )}
          {(options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          {...fieldProps}
          type={type}
          onKeyDown={(e) => onKeyDown(e, name)}
          autoComplete={autoComplete}
          autoCorrect={autoCorrect}
          autoCapitalize={autoCapitalize}
          inputMode={inputMode}
          pattern={pattern}
        />
      )}
    </section>
  );
}

export default InputField;
